import React from 'react';
import SectionHeader from './SectionHeader';
import CurrencyInput from './CurrencyInput';
import NotesInput from './NotesInput';

const MIN_SOURCES = 3;
const MAX_SOURCES = 20;
const EMPTY_SOURCE = { name: '', amount: null };

function buildSources(existing) {
  const base = existing && existing.sources ? existing.sources : [];
  const sources = base.map((s) => ({ ...EMPTY_SOURCE, ...s }));
  while (sources.length < MIN_SOURCES) sources.push({ ...EMPTY_SOURCE });
  return sources;
}

function sourceHasData(src) {
  return (src.name && src.name.trim() !== '') || (src.amount !== null && src.amount !== undefined);
}

/**
 * Section 1 — Income
 *
 * Starts with 3 rows. Source 1 is required. When the last visible row
 * gets any data, a new empty row is automatically appended.
 *
 * Sources suggested from imported statements carry `_import_key`; under
 * those, the take-home deposits are shown as a hint (the field asks for
 * gross, so the deposit total is never filled in for the client).
 *
 * @param {object}   data         - Current section data from formData.income.
 * @param {function} onChange     - Called with updated section object.
 * @param {Map}      importIncome - Payer key -> { annual, ... } from importMerge.importIncome.
 */
export default function StepIncome({ data, onChange, importIncome = new Map() }) {
  const sources = buildSources(data);

  // Auto-grow: if the last row has data, append a new empty row (cap at MAX_SOURCES)
  const visibleSources =
    sources.length > 0 &&
    sourceHasData(sources[sources.length - 1]) &&
    sources.length < MAX_SOURCES
      ? [...sources, { ...EMPTY_SOURCE }]
      : sources;

  function updateSource(idx, field, value) {
    const updated = visibleSources.map((s, i) =>
      i === idx ? { ...s, [field]: value } : s
    );
    onChange({ ...data, sources: updated });
  }

  const total = visibleSources.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

  // Highlight required fields when optional sources have data but required ones are missing
  const s0 = visibleSources[0] || EMPTY_SOURCE;
  const hasOptionalData = visibleSources.slice(1).some(sourceHasData);
  const s0NameMissing = !s0.name || s0.name.trim() === '';
  const s0AmountMissing = s0.amount === null || s0.amount === undefined || s0.amount === 0;
  const warnRequired = hasOptionalData && (s0NameMissing || s0AmountMissing);

  return (
    <div>
      <SectionHeader
        number={1}
        title="Income"
        tip="Use last year's tax return (W-2s, 1099s, Schedule K-1s) to find
          annual income figures. Enter gross income before taxes and deductions.
          Do not list one-time or unguaranteed amounts such as potential bonuses.
          Do not list interest payments from Savings or CD accounts."
      />

      {visibleSources.map((src, idx) => (
        <div key={idx} className="row g-2 mb-3">
          <div className="col-12 col-md-7 d-flex flex-column">
            <label
              className={`form-label${idx === 0 && warnRequired && s0NameMissing ? ' text-warning fw-semibold' : ''}`}
              htmlFor={`income_name_${idx}`}
            >
              Source {idx + 1} name
              {idx === 0 ? (
                <span className="text-danger ms-1">*</span>
              ) : (
                <span className="text-muted ms-1">(optional)</span>
              )}
            </label>
            <input
              id={`income_name_${idx}`}
              type="text"
              className={`form-control${idx === 0 && warnRequired && s0NameMissing ? ' border-warning border-2' : ''}`}
              placeholder={idx === 0 ? "e.g. Jane's Wages, Joe's Social Security" : `Income source ${idx + 1}`}
              value={src.name || ''}
              onChange={(e) => updateSource(idx, 'name', e.target.value)}
              required={idx === 0}
            />
            {idx === 0 && warnRequired && s0NameMissing && (
              <div className="form-text text-warning">Please fill in this required field</div>
            )}
          </div>
          <div className="col-12 col-md-5 d-flex flex-column">
            <CurrencyInput
              label={`Source ${idx + 1} annual amount${idx === 0 ? ' *' : ''}`}
              id={`income_source_${idx}`}
              value={src.amount}
              onChange={(val) => updateSource(idx, 'amount', val)}
              className="mb-0"
              highlight={idx === 0 && warnRequired && s0AmountMissing}
            />
            {idx === 0 && warnRequired && s0AmountMissing && (
              <div className="form-text text-warning">Please fill in this required field</div>
            )}
          </div>
          {src._import_key && importIncome.get(src._import_key) && (
            <div className="col-12">
              <div className="form-text text-primary">
                <i className="bi bi-info-circle me-1" />
                From your statements: about $
                {Math.round(importIncome.get(src._import_key).annual).toLocaleString()}/yr deposited
                (take-home). Enter gross pay before taxes — your W-2, Box 1.
              </div>
            </div>
          )}
        </div>
      ))}

      {total > 0 && (
        <div className="cashflow-total-box mt-3">
          <span className="fw-semibold">1: Total Annual Income</span>
          <span className="cashflow-total-amount">
            ${total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        </div>
      )}

      <NotesInput value={(data || {}).notes} onChange={(val) => onChange({ ...data, notes: val })} />
    </div>
  );
}
